import * as ToastPrimitive from '@radix-ui/react-toast';
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

interface ToastItem {
  id: string;
  title: string;
  description?: string;
  variant: 'info' | 'success' | 'error';
}

interface ToastContextValue {
  notify(title: string, options?: { description?: string; variant?: ToastItem['variant'] }): void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const notify = useCallback<ToastContextValue['notify']>((title, options = {}) => {
    const item: ToastItem = {
      id: crypto.randomUUID(),
      title,
      variant: options.variant ?? 'info',
      ...(options.description ? { description: options.description } : {})
    };
    setItems((current) => [...current, item]);
  }, []);
  const value = useMemo(() => ({ notify }), [notify]);
  return (
    <ToastContext.Provider value={value}>
      <ToastPrimitive.Provider swipeDirection="right" duration={4500}>
        {children}
        {items.map((item) => (
          <ToastPrimitive.Root
            key={item.id}
            className={`dd-toast dd-toast--${item.variant}`}
            defaultOpen
            onOpenChange={(open) => {
              if (!open) setItems((current) => current.filter(({ id }) => id !== item.id));
            }}
          >
            <ToastPrimitive.Title className="dd-toast-title">{item.title}</ToastPrimitive.Title>
            {item.description ? (
              <ToastPrimitive.Description className="dd-toast-description">
                {item.description}
              </ToastPrimitive.Description>
            ) : null}
          </ToastPrimitive.Root>
        ))}
        <ToastPrimitive.Viewport className="dd-toast-viewport" />
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const value = useContext(ToastContext);
  if (!value) throw new Error('useToast deve essere usato dentro ToastProvider');
  return value;
}
